import React, { useContext, useState, useEffect, type ReactNode } from 'react';
import parse from 'html-react-parser';

import { DraftManualDetailContext } from './context';
import Loading from 'views/App/Portal/Loading';
import ToC from './ToC';
import ChapterEditor from './ChapterEditor';
import PolicyEditor from './PolicyEditor';
import { PolicyEditorProvider } from './PolicyEditor/context';
import { PrefaceEditorProvider } from './PrefaceEditor/context';
import PrefaceEditor from './PrefaceEditor';
import { TakeoverLayout } from '_lex/_layouts/TakeoverLayout';
import ManualActions from './ManualActions';

type LayoutSlots = { menubarLeft?: ReactNode; contentHeaderRight?: ReactNode };

const DraftManualsDetailPage: React.FC = () => {
  const { manual, manualQuery, node, manualHasPermissions } = useContext(DraftManualDetailContext);
  const [layoutSlots, setLayoutSlots] = useState<LayoutSlots>({});

  useEffect(() => {
    if (!node) setLayoutSlots({});
  }, [node]);

  const renderNode = (nodeVal?: string, embedded = false) => {
    if (!nodeVal) return null;

    const [type, id] = nodeVal.split(':');
    const embeddedProps = embedded ? { embedded: true as const, setLayoutSlots } : {};
    switch (type) {
      case 'preface':
        return (
          <PrefaceEditorProvider key={id} id={id}>
            <PrefaceEditor key={id} id={id} {...embeddedProps} />
          </PrefaceEditorProvider>
        );
      case 'chapter':
        return <ChapterEditor key={id} id={id} {...embeddedProps} />;
      case 'policy':
        return (
          <PolicyEditorProvider key={id} id={id}>
            <PolicyEditor id={id} {...embeddedProps} />
          </PolicyEditorProvider>
        );
      default:
        return null;
    }
  };

  if (manualQuery.isLoading) return <Loading />;
  if (!manual?.id) return null;

  const isChapter = node?.startsWith('chapter:');
  const useSharedLayout = !node || isChapter;

  if (node && !useSharedLayout) {
    return renderNode(node);
  }

  return (
    <TakeoverLayout
      pageTitle={manual ? parse(manual.name ?? '') : ''}
      backUrl='/admin/policy/draft-manuals'
      leftDrawer={<ToC key={manual.id} />}
      menubarLeft={layoutSlots.menubarLeft ?? (manual && <ManualActions manual={manual} manualHasPermissions={manualHasPermissions} />)}
      contentHeaderRight={layoutSlots.contentHeaderRight}
    >
      {node ? renderNode(node, true) : ' '}
    </TakeoverLayout>
  );
};

export default DraftManualsDetailPage;
